export interface CategoryDictionaryMetadata {
  matchingMode: 'category';
  categories: Record<string, string[]>;
}

export interface CategoryDictionaryData {
  items: Record<string, string>;
  metadata: CategoryDictionaryMetadata;
}

export interface SortDictionaryMetadata {
  matchingMode: 'sort';
}

export interface SortDictionaryData {
  labels: string[];
  items: Record<string, string>;
  metadata: SortDictionaryMetadata;
}

export interface GameCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  data: Record<string, string> | CategoryDictionaryData | SortDictionaryData;
}

export interface TopicCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  games: GameCategory[];
}

// Math topic games
const mathGames: GameCategory[] = [
  {
    id: 'math-formulas',
    name: 'Math Formulas',
    description: 'Match mathematical formulas with their descriptions',
    icon: '📐',
    data: {
      "Surface area of a sphere": "SA = 4πr²",
      "SA = 4πr²": "Surface area of a sphere",
      "Area of a parallelogram": "A = b × h",
      "A = b × h": "Area of a parallelogram",
      "Area of a square": "A = s²",
      "A = s²": "Area of a square",
      "Perimeter of a square": "P = 4s",
      "P = 4s": "Perimeter of a square",
      "Perimeter of a rectangle": "P = 2l + 2w",
      "P = 2l + 2w": "Perimeter of a rectangle",
      "Surface area of a cube": "SA = 6s²",
      "SA = 6s²": "Surface area of a cube",
      "Area of a trapezoid": "A = ½ × (b₁ + b₂) × h",
      "A = ½ × (b₁ + b₂) × h": "Area of a trapezoid",
      "Surface area of a cylinder": "SA = 2πr² + 2πrh",
      "SA = 2πr² + 2πrh": "Surface area of a cylinder",
      "Area of a triangle": "A = ½ × b × h",
      "A = ½ × b × h": "Area of a triangle",
      "Distance formula": "d = √((x₂ - x₁)² + (y₂ - y₁)²)",
      "d = √((x₂ - x₁)² + (y₂ - y₁)²)": "Distance formula",
      "Volume of a cone": "V = ⅓ πr²h",
      "V = ⅓ πr²h": "Volume of a cone",
      "Slope formula": "m = (y₂ - y₁) / (x₂ - x₁)",
      "m = (y₂ - y₁) / (x₂ - x₁)": "Slope formula",
      "Area of a rectangle": "A = l × w",
      "A = l × w": "Area of a rectangle",
      "Perimeter of a triangle": "P = a + b + c",
      "P = a + b + c": "Perimeter of a triangle",
      "Volume of a cylinder": "V = πr²h",
      "V = πr²h": "Volume of a cylinder",
      "Volume of a sphere": "V = 4/3 πr³",
      "V = 4/3 πr³": "Volume of a sphere",
      "Simple interest": "I = P × r × t",
      "I = P × r × t": "Simple interest",
      "Volume of a rectangular prism": "V = l × w × h",
      "V = l × w × h": "Volume of a rectangular prism",
      "Circumference of a circle": "C = 2πr",
      "C = 2πr": "Circumference of a circle",
      "Area of a circle": "A = πr²",
      "A = πr²": "Area of a circle",
      "Compound interest": "A = P(1 + r/n)^(nt)",
      "A = P(1 + r/n)^(nt)": "Compound interest",
      "Quadratic formula": "x = (-b ± √(b² - 4ac)) / 2a",
      "x = (-b ± √(b² - 4ac)) / 2a": "Quadratic formula",
      "Area of an ellipse": "A = πab",
      "A = πab": "Area of an ellipse",
      "Volume of a pyramid": "V = ⅓ × base area × height",
      "V = ⅓ × base area × height": "Volume of a pyramid",
      "Surface area of a rectangular prism": "SA = 2(lw + lh + wh)",
      "SA = 2(lw + lh + wh)": "Surface area of a rectangular prism",
      "Arc length": "s = rθ",
      "s = rθ": "Arc length",
      "Area of a sector": "A = ½r²θ",
      "A = ½r²θ": "Area of a sector"
    }
  },
  {
    id: 'multiplication-table',
    name: 'Multiplication Table',
    description: 'Practice multiplication facts up to 12×12',
    icon: '✖️',
    data: {
      "1 × 1": "1", "1": "1 × 1", "1 × 2": "2", "2": "1 × 2", "1 × 3": "3", "3": "1 × 3", "1 × 4": "4", "4": "1 × 4", "1 × 5": "5", "5": "1 × 5",
      "1 × 6": "6", "6": "1 × 6", "1 × 7": "7", "7": "1 × 7", "1 × 8": "8", "8": "1 × 8", "1 × 9": "9", "9": "1 × 9", "1 × 10": "10", "10": "1 × 10",
      "1 × 11": "11", "11": "1 × 11", "1 × 12": "12", "12": "1 × 12",
      "2 × 2": "4", "2 × 3": "6", "2 × 4": "8", "2 × 5": "10", "2 × 6": "12", "2 × 7": "14", "14": "2 × 7", "2 × 8": "16", "16": "2 × 8",
      "2 × 9": "18", "18": "2 × 9", "2 × 10": "20", "20": "2 × 10", "2 × 11": "22", "22": "2 × 11", "2 × 12": "24", "24": "2 × 12",
      "3 × 3": "9", "3 × 4": "12", "3 × 5": "15", "15": "3 × 5", "3 × 6": "18", "3 × 7": "21", "21": "3 × 7", "3 × 8": "24",
      "3 × 9": "27", "27": "3 × 9", "3 × 10": "30", "30": "3 × 10", "3 × 11": "33", "33": "3 × 11", "3 × 12": "36", "36": "3 × 12",
      "4 × 4": "16", "4 × 5": "20", "4 × 6": "24", "4 × 7": "28", "28": "4 × 7", "4 × 8": "32", "32": "4 × 8", "4 × 9": "36",
      "4 × 10": "40", "40": "4 × 10", "4 × 11": "44", "44": "4 × 11", "4 × 12": "48", "48": "4 × 12",
      "5 × 5": "25", "25": "5 × 5", "5 × 6": "30", "5 × 7": "35", "35": "5 × 7", "5 × 8": "40", "5 × 9": "45", "45": "5 × 9",
      "5 × 10": "50", "50": "5 × 10", "5 × 11": "55", "55": "5 × 11", "5 × 12": "60", "60": "5 × 12",
      "6 × 6": "36", "6 × 7": "42", "42": "6 × 7", "6 × 8": "48", "6 × 9": "54", "54": "6 × 9", "6 × 10": "60",
      "6 × 11": "66", "66": "6 × 11", "6 × 12": "72", "72": "6 × 12",
      "7 × 7": "49", "49": "7 × 7", "7 × 8": "56", "56": "7 × 8", "7 × 9": "63", "63": "7 × 9", "7 × 10": "70", "70": "7 × 10",
      "7 × 11": "77", "77": "7 × 11", "7 × 12": "84", "84": "7 × 12",
      "8 × 8": "64", "64": "8 × 8", "8 × 9": "72", "8 × 10": "80", "80": "8 × 10", "8 × 11": "88", "88": "8 × 11", "8 × 12": "96", "96": "8 × 12",
      "9 × 9": "81", "81": "9 × 9", "9 × 10": "90", "90": "9 × 10", "9 × 11": "99", "99": "9 × 11", "9 × 12": "108", "108": "9 × 12",
      "10 × 10": "100", "100": "10 × 10", "10 × 11": "110", "110": "10 × 11", "10 × 12": "120", "120": "10 × 12",
      "11 × 11": "121", "121": "11 × 11", "11 × 12": "132", "132": "11 × 12",
      "12 × 12": "144", "144": "12 × 12"
    }
  },
  {
    id: 'squares-table',
    name: 'Squares Table',
    description: 'Practice perfect squares from 1² to 20²',
    icon: '🔢',
    data: {
      "1²": "1", "1": "1²", "2²": "4", "4": "2²", "3²": "9", "9": "3²", "4²": "16", "16": "4²", "5²": "25", "25": "5²",
      "6²": "36", "36": "6²", "7²": "49", "49": "7²", "8²": "64", "64": "8²", "9²": "81", "81": "9²", "10²": "100", "100": "10²",
      "11²": "121", "121": "11²", "12²": "144", "144": "12²", "13²": "169", "169": "13²", "14²": "196", "196": "14²", "15²": "225", "225": "15²",
      "16²": "256", "256": "16²", "17²": "289", "289": "17²", "18²": "324", "324": "18²", "19²": "361", "361": "19²", "20²": "400", "400": "20²"
    }
  },
  {
    id: 'pre-algebra',
    name: 'Pre-Algebra',
    description: 'Basic algebraic expressions and identities',
    icon: '🔤',
    data: {
      "a(b + c)": "ab + ac",
      "ab + ac": "a(b + c)",
      "a(b - c)": "ab - ac",
      "ab - ac": "a(b - c)",
      "(a + b)c": "ac + bc",
      "ac + bc": "(a + b)c",
      "(a - b)c": "ac - bc",
      "ac - bc": "(a - b)c",
      "(a + b)²": "a² + 2ab + b²",
      "a² + 2ab + b²": "(a + b)²",
      "(a - b)²": "a² - 2ab + b²",
      "a² - 2ab + b²": "(a - b)²",
      "(a + b)(a - b)": "a² - b²",
      "a² - b²": "(a + b)(a - b)",
      "x/(y/z)": "xz/y",
      "xz/y": "x/(y/z)",
      "(x/y)/z": "x/(yz)",
      "x/(yz)": "(x/y)/z",
      "x⁰": "1",
      "1": "x⁰",
      "x¹": "x",
      "x": "x¹",
      "xⁿ × xᵐ": "xⁿ⁺ᵐ",
      "xⁿ⁺ᵐ": "xⁿ × xᵐ",
      "xⁿ/xᵐ": "xⁿ⁻ᵐ",
      "xⁿ⁻ᵐ": "xⁿ/xᵐ",
      "(xⁿ)ᵐ": "xⁿᵐ",
      "xⁿᵐ": "(xⁿ)ᵐ",
      "(ab)ⁿ": "aⁿbⁿ",
      "aⁿbⁿ": "(ab)ⁿ",
      "(a/b)ⁿ": "aⁿ/bⁿ",
      "aⁿ/bⁿ": "(a/b)ⁿ",
      "x⁻ⁿ": "1/xⁿ",
      "1/xⁿ": "x⁻ⁿ"
    }
  }
];

// History topic games
const historyGames: GameCategory[] = [
  {
    id: 'chinese-history',
    name: 'Chinese History',
    description: 'Major dates and events in Chinese history',
    icon: '🏛️',
    data: {
      "c. 2070 BC": "Xia founded", "Xia founded": "c. 2070 BC",
      "1600 BC": "Shang founded", "Shang founded": "1600 BC",
      "1046 BC": "Zhou founded", "Zhou founded": "1046 BC",
      "770 BC": "Eastern Zhou", "Eastern Zhou": "770 BC",
      "475 BC": "Warring States", "Warring States": "475 BC",
      "221 BC": "China unified", "China unified": "221 BC",
      "206 BC": "Han founded", "Han founded": "206 BC",
      "220 AD": "Han falls", "Han falls": "220 AD",
      "581 AD": "Sui founded", "Sui founded": "581 AD",
      "618 AD": "Tang founded", "Tang founded": "618 AD",
      "907 AD": "Tang falls", "Tang falls": "907 AD",
      "960 AD": "Song founded", "Song founded": "960 AD",
      "1271 AD": "Yuan founded", "Yuan founded": "1271 AD",
      "1368 AD": "Ming founded", "Ming founded": "1368 AD",
      "1644 AD": "Qing founded", "Qing founded": "1644 AD",
      "1839 AD": "Opium War", "Opium War": "1839 AD",
      "1911 AD": "Qing falls", "Qing falls": "1911 AD",
      "1912 AD": "Republic founded", "Republic founded": "1912 AD",
      "1931 AD": "Manchuria invaded", "Manchuria invaded": "1931 AD",
      "1937 AD": "War with Japan", "War with Japan": "1937 AD",
      "1945 AD": "WWII ends", "WWII ends": "1945 AD",
      "1949 AD": "PRC founded", "PRC founded": "1949 AD",
      "1958 AD": "Great Leap", "Great Leap": "1958 AD",
      "1966 AD": "Cultural Revolution", "Cultural Revolution": "1966 AD",
      "1976 AD": "Mao dies", "Mao dies": "1976 AD",
      "1978 AD": "Reform begins", "Reform begins": "1978 AD",
      "1989 AD": "Tiananmen", "Tiananmen": "1989 AD",
      "1997 AD": "Hong Kong", "Hong Kong": "1997 AD",
      "2008 AD": "Olympics", "Olympics": "2008 AD",
      "2022 AD": "Zero-COVID", "Zero-COVID": "2022 AD",
      "551 BC": "Confucius born", "Confucius born": "551 BC",
      "259 BC": "Qin Shi Huang born", "Qin Shi Huang born": "259 BC",
      "210 BC": "Terracotta Army built", "Terracotta Army built": "210 BC",
      "1405 AD": "Zheng He voyages", "Zheng He voyages": "1405 AD"
    }
  },
  {
    id: 'russian-history',
    name: 'Russian History',
    description: 'Key dates and events in Russian history',
    icon: '🇷🇺',
    data: {
      "862": "Rurik invited", "Rurik invited": "862",
      "988": "Christianization Kievan", "Christianization Kievan": "988",
      "1240": "Mongol invasion", "Mongol invasion": "1240",
      "1480": "Mongol yoke ends", "Mongol yoke ends": "1480",
      "1547": "Tsardom begins", "Tsardom begins": "1547",
      "1613": "Romanovs begin", "Romanovs begin": "1613",
      "1703": "Petersburg founded", "Petersburg founded": "1703",
      "1721": "Empire proclaimed", "Empire proclaimed": "1721",
      "1812": "Napoleonic invasion", "Napoleonic invasion": "1812",
      "1861": "Serfs freed", "Serfs freed": "1861",
      "1905": "First revolution", "First revolution": "1905",
      "1914": "WWI begins", "WWI begins": "1914",
      "1917": "Revolution begins", "Revolution begins": "1917",
      "1918": "Tsar executed", "Tsar executed": "1918",
      "1922": "USSR founded", "USSR founded": "1922",
      "1939": "WWII begins", "WWII begins": "1939",
      "1941": "Moscow battle", "Moscow battle": "1941",
      "1941-44": "Leningrad siege", "Leningrad siege": "1941-44",
      "1945": "WWII ends", "WWII ends": "1945",
      "1957": "Sputnik launched", "Sputnik launched": "1957",
      "1961": "Gagarin flight", "Gagarin flight": "1961",
      "1979": "Afghan invasion", "Afghan invasion": "1979",
      "1980": "Moscow Olympics", "Moscow Olympics": "1980",
      "1986": "Chernobyl disaster", "Chernobyl disaster": "1986",
      "1991": "USSR dissolved", "USSR dissolved": "1991",
      "1993": "Constitution adopted", "Constitution adopted": "1993",
      "2000": "Putin elected", "Putin elected": "2000",
      "2014": "Crimea annexed", "Crimea annexed": "2014",
      "2018": "World Cup", "World Cup": "2018",
      "2022": "Ukraine invasion", "Ukraine invasion": "2022",
      "1169": "Kiev sacked", "Kiev sacked": "1169",
      "1380": "Kulikovo Battle", "Kulikovo Battle": "1380",
      "1598": "Time of Troubles", "Time of Troubles": "1598",
      "1649": "Serfdom codified", "Serfdom codified": "1649",
      "1762": "Catherine II", "Catherine II": "1762",
      "1825": "Decembrist revolt", "Decembrist revolt": "1825",
      "1853": "Crimean War", "Crimean War": "1853"
    }
  },
  {
    id: 'historical-figures',
    name: 'Historical Figures',
    description: 'Match famous historical figures with their descriptions',
    icon: '📜',
    data: {
      "Albert Einstein": "German-born theoretical physicist who developed the theory of relativity",
      "German-born theoretical physicist who developed the theory of relativity": "Albert Einstein",
      "Leonardo da Vinci": "Italian polymath during the Renaissance known for art and inventions",
      "Italian polymath during the Renaissance known for art and inventions": "Leonardo da Vinci",
      "William Shakespeare": "English playwright and poet widely regarded as the greatest writer",
      "English playwright and poet widely regarded as the greatest writer": "William Shakespeare",
      "Marie Curie": "Polish-French physicist and chemist who discovered polonium and radium",
      "Polish-French physicist and chemist who discovered polonium and radium": "Marie Curie",
      "Napoleon Bonaparte": "French military general and emperor who conquered much of Europe",
      "French military general and emperor who conquered much of Europe": "Napoleon Bonaparte",
      "Cleopatra VII": "Last active pharaoh of Ancient Egypt known for her intelligence and beauty",
      "Last active pharaoh of Ancient Egypt known for her intelligence and beauty": "Cleopatra VII",
      "Abraham Lincoln": "16th President of the United States who preserved the Union during Civil War",
      "16th President of the United States who preserved the Union during Civil War": "Abraham Lincoln",
      "George Washington": "First President of the United States and Founding Father",
      "First President of the United States and Founding Father": "George Washington",
      "Isaac Newton": "English mathematician and physicist who formulated laws of motion and gravity",
      "English mathematician and physicist who formulated laws of motion and gravity": "Isaac Newton",
      "Alexander the Great": "Ancient Greek king who created one of the largest empires in history",
      "Ancient Greek king who created one of the largest empires in history": "Alexander the Great",
      "Julius Caesar": "Roman general and statesman who played a critical role in Roman Empire",
      "Roman general and statesman who played a critical role in Roman Empire": "Julius Caesar",
      "Winston Churchill": "British Prime Minister who led Britain during World War II",
      "British Prime Minister who led Britain during World War II": "Winston Churchill",
      "Martin Luther King Jr.": "American civil rights leader who fought for racial equality",
      "American civil rights leader who fought for racial equality": "Martin Luther King Jr.",
      "Mahatma Gandhi": "Indian independence activist who used nonviolent civil disobedience",
      "Indian independence activist who used nonviolent civil disobedience": "Mahatma Gandhi",
      "Christopher Columbus": "Italian explorer whose voyages opened the Americas to European exploration",
      "Italian explorer whose voyages opened the Americas to European exploration": "Christopher Columbus",
      "Thomas Edison": "American inventor who developed many devices including the light bulb",
      "American inventor who developed many devices including the light bulb": "Thomas Edison",
      "Vincent van Gogh": "Dutch post-impressionist painter known for his colorful and emotional works",
      "Dutch post-impressionist painter known for his colorful and emotional works": "Vincent van Gogh",
      "Mozart": "Austrian composer who wrote over 600 pieces during the Classical period",
      "Austrian composer who wrote over 600 pieces during the Classical period": "Mozart",
      "Beethoven": "German composer who continued to compose despite losing his hearing",
      "German composer who continued to compose despite losing his hearing": "Beethoven",
      "Pablo Picasso": "Spanish artist who co-founded the Cubist movement in art",
      "Spanish artist who co-founded the Cubist movement in art": "Pablo Picasso"
    }
  }
];

// Language topic games
const languageGames: GameCategory[] = [
  {
    id: 'chinese-characters-beginner',
    name: 'Chinese Characters (Beginner)',
    description: 'Learn basic Chinese characters with pinyin pronunciation',
    icon: '🇨🇳',
    data: {
      "一": "one (yi1)", "one (yi1)": "一", "二": "two (er4)", "two (er4)": "二", "三": "three (san1)", "three (san1)": "三",
      "四": "four (si4)", "four (si4)": "四", "五": "five (wu3)", "five (wu3)": "五", "六": "six (liu4)", "six (liu4)": "六",
      "七": "seven (qi1)", "seven (qi1)": "七", "八": "eight (ba1)", "eight (ba1)": "八", "九": "nine (jiu3)", "nine (jiu3)": "九",
      "十": "ten (shi2)", "ten (shi2)": "十", "人": "person (ren2)", "person (ren2)": "人", "口": "mouth (kou3)", "mouth (kou3)": "口",
      "目": "eye (mu4)", "eye (mu4)": "目", "耳": "ear (er3)", "ear (er3)": "耳", "手": "hand (shou3)", "hand (shou3)": "手",
      "足": "foot (zu2)", "foot (zu2)": "足", "心": "heart (xin1)", "heart (xin1)": "心", "日": "sun (ri4)", "sun (ri4)": "日",
      "月": "moon (yue4)", "moon (yue4)": "月", "水": "water (shui3)", "water (shui3)": "水", "火": "fire (huo3)", "fire (huo3)": "火",
      "木": "wood (mu4)", "wood (mu4)": "木", "土": "earth (tu3)", "earth (tu3)": "土", "山": "mountain (shan1)", "mountain (shan1)": "山",
      "田": "field (tian2)", "field (tian2)": "田", "女": "woman (nv3)", "woman (nv3)": "女", "子": "child (zi3)", "child (zi3)": "子",
      "大": "big (da4)", "big (da4)": "大", "小": "small (xiao3)", "small (xiao3)": "小", "中": "middle (zhong1)", "middle (zhong1)": "中"
    }
  },
  {
    id: 'chinese-characters-intermediate',
    name: 'Chinese Characters (Intermediate)',
    description: 'Common Chinese characters for everyday vocabulary',
    icon: '🏮',
    data: {
      "家": "home (jia1)", "home (jia1)": "家", "国": "nation (guo2)", "nation (guo2)": "国", "校": "school (xiao4)", "school (xiao4)": "校",
      "学": "study (xue2)", "study (xue2)": "学", "老": "old (lao3)", "old (lao3)": "老", "师": "teacher (shi1)", "teacher (shi1)": "师",
      "生": "life (sheng1)", "life (sheng1)": "生", "医": "doctor (yi1)", "doctor (yi1)": "医", "书": "book (shu1)", "book (shu1)": "书",
      "文": "script (wen2)", "script (wen2)": "文", "语": "language (yu3)", "language (yu3)": "语", "说": "speak (shuo1)", "speak (shuo1)": "说",
      "话": "speech (hua4)", "speech (hua4)": "话", "写": "write (xie3)", "write (xie3)": "写", "读": "read (du2)", "read (du2)": "读",
      "看": "see (kan4)", "see (kan4)": "看", "听": "hear (ting1)", "hear (ting1)": "听", "给": "give (gei3)", "give (gei3)": "给",
      "来": "come (lai2)", "come (lai2)": "来", "去": "go (qu4)", "go (qu4)": "去", "开": "open (kai1)", "open (kai1)": "开",
      "关": "close (guan1)", "close (guan1)": "关", "高": "tall (gao1)", "tall (gao1)": "高", "长": "long (chang2)", "long (chang2)": "长",
      "多": "many (duo1)", "many (duo1)": "多", "少": "few (shao3)", "few (shao3)": "少", "新": "new (xin1)", "new (xin1)": "新",
      "朋": "friend (peng2)", "friend (peng2)": "朋", "友": "friend (you3)", "friend (you3)": "友", "电": "electric (dian4)", "electric (dian4)": "电"
    }
  },
  {
    id: 'chinese-characters-advanced',
    name: 'Chinese Characters (Advanced)',
    description: 'Advanced Chinese characters for philosophy and culture',
    icon: '📚',
    data: {
      "德": "virtue (de2)", "virtue (de2)": "德", "义": "righteousness (yi4)", "righteousness (yi4)": "义", "礼": "ritual (li3)", "ritual (li3)": "礼",
      "法": "law (fa3)", "law (fa3)": "法", "仁": "benevolence (ren2)", "benevolence (ren2)": "仁", "智": "wisdom (zhi4)", "wisdom (zhi4)": "智",
      "信": "trust (xin4)", "trust (xin4)": "信", "道": "way (dao4)", "way (dao4)": "道", "诗": "poetry (shi1)", "poetry (shi1)": "诗",
      "史": "history (shi3)", "history (shi3)": "史", "哲": "philosophy (zhe2)", "philosophy (zhe2)": "哲", "思": "think (si1)", "think (si1)": "思",
      "经": "classic (jing1)", "classic (jing1)": "经", "传": "transmit (chuan2)", "transmit (chuan2)": "传", "统": "unify (tong3)", "unify (tong3)": "统",
      "观": "observe (guan1)", "observe (guan1)": "观", "念": "notion (nian4)", "notion (nian4)": "念", "逻": "logic (luo2)", "logic (luo2)": "逻",
      "辩": "debate (bian4)", "debate (bian4)": "辩", "证": "proof (zheng4)", "proof (zheng4)": "证", "美": "beauty (mei3)", "beauty (mei3)": "美",
      "丑": "ugly (chou3)", "ugly (chou3)": "丑", "善": "good (shan4)", "good (shan4)": "善", "恶": "evil (e4)", "evil (e4)": "恶",
      "悟": "enlighten (wu4)", "enlighten (wu4)": "悟", "境": "realm (jing4)", "realm (jing4)": "境", "权": "power (quan2)", "power (quan2)": "权",
      "利": "benefit (li4)", "benefit (li4)": "利", "民": "people (min2)", "people (min2)": "民", "主": "master (zhu3)", "master (zhu3)": "主"
    }
  },
  {
    id: 'english-russian',
    name: 'English ↔ Russian',
    description: 'Match English words with Russian translations',
    icon: '🇷🇺',
    data: {
      'hello': 'привет', 'goodbye': 'до свидания', 'thank you': 'спасибо', 'yes': 'да', 'no': 'нет', 'water': 'вода', 'food': 'еда',
      'house': 'дом', 'car': 'машина', 'book': 'книга', 'phone': 'телефон', 'computer': 'компьютер', 'school': 'школа', 'work': 'работа',
      'family': 'семья', 'friend': 'друг', 'love': 'любовь', 'happy': 'счастливый', 'sad': 'грустный', 'big': 'большой', 'small': 'маленький',
      'hot': 'горячий', 'cold': 'холодный', 'good': 'хороший', 'bad': 'плохой', 'beautiful': 'красивый', 'fast': 'быстрый', 'slow': 'медленный',
      'new': 'новый', 'old': 'старый', 'red': 'красный', 'blue': 'синий',
      'привет': 'hello', 'до свидания': 'goodbye', 'спасибо': 'thank you', 'да': 'yes', 'нет': 'no', 'вода': 'water', 'еда': 'food',
      'дом': 'house', 'машина': 'car', 'книга': 'book', 'телефон': 'phone', 'компьютер': 'computer', 'школа': 'school', 'работа': 'work',
      'семья': 'family', 'друг': 'friend', 'любовь': 'love', 'счастливый': 'happy', 'грустный': 'sad', 'большой': 'big', 'маленький': 'small',
      'горячий': 'hot', 'холодный': 'cold', 'хороший': 'good', 'плохой': 'bad', 'красивый': 'beautiful', 'быстрый': 'fast', 'медленный': 'slow',
      'новый': 'new', 'старый': 'old', 'красный': 'red', 'синий': 'blue'
    }
  }
];

// Miscellaneous topic games
const miscellaneousGames: GameCategory[] = [
  {
    id: 'numbers',
    name: 'Number Words',
    description: 'Match numbers with their written forms',
    icon: '🔢',
    data: {
      '1': 'one', '2': 'two', '3': 'three', '4': 'four', '5': 'five', '6': 'six', '7': 'seven', '8': 'eight', '9': 'nine', '10': 'ten',
      '11': 'eleven', '12': 'twelve', '13': 'thirteen', '14': 'fourteen', '15': 'fifteen', '16': 'sixteen', '17': 'seventeen', '18': 'eighteen',
      '19': 'nineteen', '20': 'twenty', '21': 'twenty-one', '25': 'twenty-five', '30': 'thirty', '40': 'forty', '50': 'fifty', '60': 'sixty',
      '70': 'seventy', '80': 'eighty', '90': 'ninety', '100': 'one hundred',
      'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10',
      'eleven': '11', 'twelve': '12', 'thirteen': '13', 'fourteen': '14', 'fifteen': '15', 'sixteen': '16', 'seventeen': '17', 'eighteen': '18',
      'nineteen': '19', 'twenty': '20', 'twenty-one': '21', 'twenty-five': '25', 'thirty': '30', 'forty': '40', 'fifty': '50', 'sixty': '60',
      'seventy': '70', 'eighty': '80', 'ninety': '90', 'one hundred': '100'
    }
  },
  {
    id: 'animals',
    name: 'Animals & Emojis',
    description: 'Match animal names with their emoji representations',
    icon: '🐾',
    data: {
      'cat': '🐱', 'dog': '🐶', 'mouse': '🐭', 'hamster': '🐹', 'rabbit': '🐰', 'fox': '🦊', 'bear': '🐻', 'panda': '🐼', 'koala': '🐨',
      'tiger': '🐯', 'lion': '🦁', 'cow': '🐮', 'pig': '🐷', 'frog': '🐸', 'monkey': '🐵', 'chicken': '🐔', 'penguin': '🐧', 'bird': '🐦',
      'duck': '🦆', 'eagle': '🦅', 'owl': '🦉', 'bat': '🦇', 'wolf': '🐺', 'horse': '🐴', 'unicorn': '🦄', 'zebra': '🦓', 'deer': '🦌',
      'giraffe': '🦒', 'elephant': '🐘', 'rhino': '🦏', 'hippo': '🦛', 'crocodile': '🐊', 'turtle': '🐢', 'lizard': '🦎', 'snake': '🐍',
      'dragon': '🐲', 'whale': '🐳', 'dolphin': '🐬', 'fish': '🐟', 'shark': '🦈', 'octopus': '🐙', 'butterfly': '🦋', 'bee': '🐝', 'ant': '🐜',
      'spider': '🕷️', 'scorpion': '🦂', 'crab': '🦀', 'lobster': '🦞', 'shrimp': '🦐',
      '🐱': 'cat', '🐶': 'dog', '🐭': 'mouse', '🐹': 'hamster', '🐰': 'rabbit', '🦊': 'fox', '🐻': 'bear', '🐼': 'panda', '🐨': 'koala',
      '🐯': 'tiger', '🦁': 'lion', '🐮': 'cow', '🐷': 'pig', '🐸': 'frog', '🐵': 'monkey', '🐔': 'chicken', '🐧': 'penguin', '🐦': 'bird',
      '🦆': 'duck', '🦅': 'eagle', '🦉': 'owl', '🦇': 'bat', '🐺': 'wolf', '🐴': 'horse', '🦄': 'unicorn', '🦓': 'zebra', '🦌': 'deer',
      '🦒': 'giraffe', '🐘': 'elephant', '🦏': 'rhino', '🦛': 'hippo', '🐊': 'crocodile', '🐢': 'turtle', '🦎': 'lizard', '🐍': 'snake',
      '🐲': 'dragon', '🐳': 'whale', '🐬': 'dolphin', '🐟': 'fish', '🦈': 'shark', '🐙': 'octopus', '🦋': 'butterfly', '🐝': 'bee', '🐜': 'ant',
      '🕷️': 'spider', '🦂': 'scorpion', '🦀': 'crab', '🦞': 'lobster', '🦐': 'shrimp'
    }
  },
  {
    id: 'countries',
    name: 'Countries & Codes',
    description: 'Match country names with their ISO codes',
    icon: '🌍',
    data: {
      'United States': 'USA', 'Canada': 'CAN', 'Mexico': 'MEX', 'Brazil': 'BRA', 'Argentina': 'ARG', 'United Kingdom': 'GBR', 'France': 'FRA',
      'Germany': 'DEU', 'Italy': 'ITA', 'Spain': 'ESP', 'Netherlands': 'NLD', 'Belgium': 'BEL', 'Switzerland': 'CHE', 'Austria': 'AUT',
      'Sweden': 'SWE', 'Norway': 'NOR', 'Denmark': 'DNK', 'Finland': 'FIN', 'Russia': 'RUS', 'Poland': 'POL', 'Czech Republic': 'CZE',
      'Hungary': 'HUN', 'Romania': 'ROU', 'Greece': 'GRC', 'Turkey': 'TUR', 'China': 'CHN', 'Japan': 'JPN', 'South Korea': 'KOR',
      'India': 'IND', 'Thailand': 'THA', 'Vietnam': 'VNM', 'Philippines': 'PHL', 'Indonesia': 'IDN', 'Malaysia': 'MYS', 'Singapore': 'SGP',
      'Australia': 'AUS', 'New Zealand': 'NZL', 'South Africa': 'ZAF', 'Egypt': 'EGY', 'Morocco': 'MAR', 'Nigeria': 'NGA', 'Kenya': 'KEN',
      'Israel': 'ISR', 'Saudi Arabia': 'SAU', 'UAE': 'ARE', 'Chile': 'CHL', 'Peru': 'PER', 'Colombia': 'COL', 'Venezuela': 'VEN',
      'USA': 'United States', 'CAN': 'Canada', 'MEX': 'Mexico', 'BRA': 'Brazil', 'ARG': 'Argentina', 'GBR': 'United Kingdom', 'FRA': 'France',
      'DEU': 'Germany', 'ITA': 'Italy', 'ESP': 'Spain', 'NLD': 'Netherlands', 'BEL': 'Belgium', 'CHE': 'Switzerland', 'AUT': 'Austria',
      'SWE': 'Sweden', 'NOR': 'Norway', 'DNK': 'Denmark', 'FIN': 'Finland', 'RUS': 'Russia', 'POL': 'Poland', 'CZE': 'Czech Republic',
      'HUN': 'Hungary', 'ROU': 'Romania', 'GRC': 'Greece', 'TUR': 'Turkey', 'CHN': 'China', 'JPN': 'Japan', 'KOR': 'South Korea',
      'IND': 'India', 'THA': 'Thailand', 'VNM': 'Vietnam', 'PHL': 'Philippines', 'IDN': 'Indonesia', 'MYS': 'Malaysia', 'SGP': 'Singapore',
      'AUS': 'Australia', 'NZL': 'New Zealand', 'ZAF': 'South Africa', 'EGY': 'Egypt', 'MAR': 'Morocco', 'NGA': 'Nigeria', 'KEN': 'Kenya',
      'ISR': 'Israel', 'SAU': 'Saudi Arabia', 'ARE': 'UAE', 'CHL': 'Chile', 'PER': 'Peru', 'COL': 'Colombia', 'VEN': 'Venezuela'
    }
  }
];

// Science topic games  
const scienceGames: GameCategory[] = [
  {
    id: 'periodic-elements',
    name: 'Periodic Elements',
    description: 'Match chemical elements with their symbols',
    icon: '⚛️',
    data: {
      "Hydrogen": "H", "H": "Hydrogen", "Helium": "He", "He": "Helium", "Lithium": "Li", "Li": "Lithium", "Beryllium": "Be", "Be": "Beryllium",
      "Boron": "B", "B": "Boron", "Carbon": "C", "C": "Carbon", "Nitrogen": "N", "N": "Nitrogen", "Oxygen": "O", "O": "Oxygen",
      "Fluorine": "F", "F": "Fluorine", "Neon": "Ne", "Ne": "Neon", "Sodium": "Na", "Na": "Sodium", "Magnesium": "Mg", "Mg": "Magnesium",
      "Aluminum": "Al", "Al": "Aluminum", "Silicon": "Si", "Si": "Silicon", "Phosphorus": "P", "P": "Phosphorus", "Sulfur": "S", "S": "Sulfur",
      "Chlorine": "Cl", "Cl": "Chlorine", "Argon": "Ar", "Ar": "Argon", "Potassium": "K", "K": "Potassium", "Calcium": "Ca", "Ca": "Calcium",
      "Iron": "Fe", "Fe": "Iron", "Copper": "Cu", "Cu": "Copper", "Zinc": "Zn", "Zn": "Zinc", "Silver": "Ag", "Ag": "Silver",
      "Gold": "Au", "Au": "Gold", "Mercury": "Hg", "Hg": "Mercury", "Lead": "Pb", "Pb": "Lead", "Uranium": "U", "U": "Uranium"
    }
  },
  {
    id: 'physics-formulas',
    name: 'Physics Formulas',
    description: 'Essential physics equations and laws',
    icon: '⚡',
    data: {
      "Force": "F = ma", "F = ma": "Force",
      "Kinetic Energy": "KE = ½mv²", "KE = ½mv²": "Kinetic Energy",
      "Potential Energy": "PE = mgh", "PE = mgh": "Potential Energy",
      "Power": "P = W/t", "P = W/t": "Power",
      "Momentum": "p = mv", "p = mv": "Momentum",
      "Ohm's Law": "V = IR", "V = IR": "Ohm's Law",
      "Einstein's Mass-Energy": "E = mc²", "E = mc²": "Einstein's Mass-Energy",
      "Gravitational Force": "F = Gm₁m₂/r²", "F = Gm₁m₂/r²": "Gravitational Force",
      "Wave Speed": "v = fλ", "v = fλ": "Wave Speed",
      "Pressure": "P = F/A", "P = F/A": "Pressure",
      "Density": "ρ = m/V", "ρ = m/V": "Density",
      "Acceleration": "a = Δv/Δt", "a = Δv/Δt": "Acceleration",
      "Work": "W = Fd", "W = Fd": "Work",
      "Electric Field": "E = F/q", "E = F/q": "Electric Field",
      "Coulomb's Law": "F = kq₁q₂/r²", "F = kq₁q₂/r²": "Coulomb's Law"
    }
  },
  {
    id: 'human-body',
    name: 'Human Body Systems',
    description: 'Major organs and body systems',
    icon: '🫀',
    data: {
      "Heart": "Circulatory System", "Circulatory System": "Heart",
      "Lungs": "Respiratory System", "Respiratory System": "Lungs",
      "Stomach": "Digestive System", "Digestive System": "Stomach",
      "Brain": "Nervous System", "Nervous System": "Brain",
      "Kidneys": "Excretory System", "Excretory System": "Kidneys",
      "Liver": "Digestive & Detox", "Digestive & Detox": "Liver",
      "Bones": "Skeletal System", "Skeletal System": "Bones",
      "Muscles": "Muscular System", "Muscular System": "Muscles",
      "Skin": "Integumentary System", "Integumentary System": "Skin",
      "Thyroid": "Endocrine System", "Endocrine System": "Thyroid",
      "Spinal Cord": "Central Nervous System", "Central Nervous System": "Spinal Cord",
      "Red Blood Cells": "Oxygen Transport", "Oxygen Transport": "Red Blood Cells",
      "White Blood Cells": "Immune Defense", "Immune Defense": "White Blood Cells",
      "Pancreas": "Blood Sugar Regulation", "Blood Sugar Regulation": "Pancreas",
      "Small Intestine": "Nutrient Absorption", "Nutrient Absorption": "Small Intestine"
    }
  },
  {
    id: 'astronomy',
    name: 'Solar System & Stars',
    description: 'Planets, stars, and celestial objects',
    icon: '🌌',
    data: {
      "Mercury": "Closest to Sun", "Closest to Sun": "Mercury",
      "Venus": "Hottest Planet", "Hottest Planet": "Venus",
      "Earth": "Third from Sun", "Third from Sun": "Earth",
      "Mars": "Red Planet", "Red Planet": "Mars",
      "Jupiter": "Largest Planet", "Largest Planet": "Jupiter",
      "Saturn": "Ringed Planet", "Ringed Planet": "Saturn",
      "Uranus": "Tilted Planet", "Tilted Planet": "Uranus",
      "Neptune": "Furthest Planet", "Furthest Planet": "Neptune",
      "Sun": "Our Star", "Our Star": "Sun",
      "Moon": "Earth's Satellite", "Earth's Satellite": "Moon",
      "Milky Way": "Our Galaxy", "Our Galaxy": "Milky Way",
      "Asteroid Belt": "Between Mars & Jupiter", "Between Mars & Jupiter": "Asteroid Belt",
      "Comet": "Icy Space Rock", "Icy Space Rock": "Comet",
      "Black Hole": "Collapsed Star", "Collapsed Star": "Black Hole",
      "Supernova": "Exploding Star", "Exploding Star": "Supernova"
    }
  },
  {
    id: 'scientific-units',
    name: 'Scientific Units',
    description: 'Measurements and their units',
    icon: '📏',
    data: {
      "Length": "Meter (m)", "Meter (m)": "Length",
      "Mass": "Kilogram (kg)", "Kilogram (kg)": "Mass",
      "Time": "Second (s)", "Second (s)": "Time",
      "Temperature": "Kelvin (K)", "Kelvin (K)": "Temperature",
      "Electric Current": "Ampere (A)", "Ampere (A)": "Electric Current",
      "Force": "Newton (N)", "Newton (N)": "Force",
      "Energy": "Joule (J)", "Joule (J)": "Energy",
      "Power": "Watt (W)", "Watt (W)": "Power",
      "Pressure": "Pascal (Pa)", "Pascal (Pa)": "Pressure",
      "Frequency": "Hertz (Hz)", "Hertz (Hz)": "Frequency",
      "Voltage": "Volt (V)", "Volt (V)": "Voltage",
      "Resistance": "Ohm (Ω)", "Ohm (Ω)": "Resistance",
      "Capacitance": "Farad (F)", "Farad (F)": "Capacitance",
      "Magnetic Field": "Tesla (T)", "Tesla (T)": "Magnetic Field",
      "Luminous Intensity": "Candela (cd)", "Candela (cd)": "Luminous Intensity"
    }
  },
  {
    id: 'biology-basics',
    name: 'Biology Fundamentals',
    description: 'Basic biological concepts and terms',
    icon: '🧬',
    data: {
      "DNA": "Genetic Material", "Genetic Material": "DNA",
      "RNA": "Protein Synthesis", "Protein Synthesis": "RNA",
      "Cell": "Basic Unit of Life", "Basic Unit of Life": "Cell",
      "Mitochondria": "Cell Powerhouse", "Cell Powerhouse": "Mitochondria",
      "Nucleus": "Cell Control Center", "Cell Control Center": "Nucleus",
      "Chloroplast": "Plant Photosynthesis", "Plant Photosynthesis": "Chloroplast",
      "Ribosome": "Protein Factory", "Protein Factory": "Ribosome",
      "Enzyme": "Biological Catalyst", "Biological Catalyst": "Enzyme",
      "Chromosome": "DNA Package", "DNA Package": "Chromosome",
      "Gene": "Heredity Unit", "Heredity Unit": "Gene",
      "Photosynthesis": "Light to Sugar", "Light to Sugar": "Photosynthesis",
      "Respiration": "Sugar to Energy", "Sugar to Energy": "Respiration",
      "Evolution": "Species Change", "Species Change": "Evolution",
      "Ecosystem": "Living Community", "Living Community": "Ecosystem",
      "Biodiversity": "Life Variety", "Life Variety": "Biodiversity"
    }
  }
];

// Topic categories are defined below after category games

// ========================================
// SORT & CLASSIFY GAMES (Item-to-Label)
// ========================================

const sortGames: GameCategory[] = [
  {
    id: 'continents',
    name: 'Countries → Continents',
    description: 'Sort countries into their continents',
    icon: '🌍',
    data: {
      labels: ['Asia', 'Europe', 'Africa', 'North America', 'South America', 'Oceania'],
      items: {
        'China': 'Asia',
        'India': 'Asia',
        'Japan': 'Asia',
        'South Korea': 'Asia',
        'Thailand': 'Asia',
        'France': 'Europe',
        'Germany': 'Europe',
        'Spain': 'Europe',
        'Italy': 'Europe',
        'United Kingdom': 'Europe',
        'Egypt': 'Africa',
        'Kenya': 'Africa',
        'Nigeria': 'Africa',
        'South Africa': 'Africa',
        'Morocco': 'Africa',
        'USA': 'North America',
        'Canada': 'North America',
        'Mexico': 'North America',
        'Brazil': 'South America',
        'Argentina': 'South America',
        'Chile': 'South America',
        'Australia': 'Oceania',
        'New Zealand': 'Oceania'
      },
      metadata: {
        matchingMode: 'sort' as const
      }
    }
  },
  {
    id: 'animal-classes',
    name: 'Animals → Classes',
    description: 'Classify animals by biological class',
    icon: '🦎',
    data: {
      labels: ['Mammals', 'Birds', 'Reptiles', 'Amphibians', 'Fish'],
      items: {
        'Dog': 'Mammals',
        'Cat': 'Mammals',
        'Elephant': 'Mammals',
        'Whale': 'Mammals',
        'Bat': 'Mammals',
        'Eagle': 'Birds',
        'Parrot': 'Birds',
        'Penguin': 'Birds',
        'Owl': 'Birds',
        'Snake': 'Reptiles',
        'Lizard': 'Reptiles',
        'Turtle': 'Reptiles',
        'Crocodile': 'Reptiles',
        'Frog': 'Amphibians',
        'Salamander': 'Amphibians',
        'Toad': 'Amphibians',
        'Shark': 'Fish',
        'Tuna': 'Fish',
        'Salmon': 'Fish',
        'Goldfish': 'Fish'
      },
      metadata: {
        matchingMode: 'sort' as const
      }
    }
  },
  {
    id: 'programming-paradigms',
    name: 'Languages → Paradigms',
    description: 'Sort programming languages by primary paradigm',
    icon: '💻',
    data: {
      labels: ['Object-Oriented', 'Functional', 'Scripting'],
      items: {
        'Java': 'Object-Oriented',
        'C++': 'Object-Oriented',
        'C#': 'Object-Oriented',
        'Swift': 'Object-Oriented',
        'Haskell': 'Functional',
        'Scala': 'Functional',
        'Clojure': 'Functional',
        'Erlang': 'Functional',
        'Python': 'Scripting',
        'JavaScript': 'Scripting',
        'Ruby': 'Scripting',
        'PHP': 'Scripting',
        'Perl': 'Scripting'
      },
      metadata: {
        matchingMode: 'sort' as const
      }
    }
  },
  {
    id: 'food-groups',
    name: 'Foods → Food Groups',
    description: 'Classify foods into food groups',
    icon: '🍎',
    data: {
      labels: ['Fruits', 'Vegetables', 'Grains', 'Protein', 'Dairy'],
      items: {
        'Apple': 'Fruits',
        'Banana': 'Fruits',
        'Orange': 'Fruits',
        'Strawberry': 'Fruits',
        'Carrot': 'Vegetables',
        'Broccoli': 'Vegetables',
        'Spinach': 'Vegetables',
        'Lettuce': 'Vegetables',
        'Bread': 'Grains',
        'Rice': 'Grains',
        'Pasta': 'Grains',
        'Oats': 'Grains',
        'Chicken': 'Protein',
        'Fish': 'Protein',
        'Beans': 'Protein',
        'Eggs': 'Protein',
        'Milk': 'Dairy',
        'Cheese': 'Dairy',
        'Yogurt': 'Dairy',
        'Butter': 'Dairy'
      },
      metadata: {
        matchingMode: 'sort' as const
      }
    }
  },
  {
    id: 'geometric-polygons',
    name: 'Shapes → Polygon Types',
    description: 'Classify shapes by number of sides',
    icon: '🔷',
    data: {
      labels: ['Triangles', 'Quadrilaterals', 'Pentagons', 'Hexagons'],
      items: {
        'Equilateral Triangle': 'Triangles',
        'Right Triangle': 'Triangles',
        'Isosceles Triangle': 'Triangles',
        'Square': 'Quadrilaterals',
        'Rectangle': 'Quadrilaterals',
        'Rhombus': 'Quadrilaterals',
        'Trapezoid': 'Quadrilaterals',
        'Parallelogram': 'Quadrilaterals',
        'Pentagon': 'Pentagons',
        'Regular Pentagon': 'Pentagons',
        'Hexagon': 'Hexagons',
        'Regular Hexagon': 'Hexagons',
        'Honeycomb Shape': 'Hexagons'
      },
      metadata: {
        matchingMode: 'sort' as const
      }
    }
  }
];

// ========================================
// CATEGORY MATCHING GAMES (Multiple-to-One)
// ========================================

const categoryGames: GameCategory[] = [
  {
    id: 'cities-to-countries',
    name: 'Cities → Countries',
    description: 'Match cities to their countries (Category Mode)',
    icon: '🌍',
    data: {
      items: {
        "Paris": "Paris", "Lyon": "Lyon", "Marseille": "Marseille",
        "Berlin": "Berlin", "Munich": "Munich", "Hamburg": "Hamburg",
        "Rome": "Rome", "Milan": "Milan", "Venice": "Venice",
        "Tokyo": "Tokyo", "Osaka": "Osaka", "Kyoto": "Kyoto",
        "New York": "New York", "Los Angeles": "Los Angeles", "Chicago": "Chicago",
        "France": "France", "Germany": "Germany", "Italy": "Italy", "Japan": "Japan", "USA": "USA"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "France": ["Paris", "Lyon", "Marseille"],
          "Germany": ["Berlin", "Munich", "Hamburg"],
          "Italy": ["Rome", "Milan", "Venice"],
          "Japan": ["Tokyo", "Osaka", "Kyoto"],
          "USA": ["New York", "Los Angeles", "Chicago"]
        }
      }
    }
  },
  {
    id: 'animals-to-habitats',
    name: 'Animals → Habitats',
    description: 'Match animals to their natural habitats (Category Mode)',
    icon: '🦁',
    data: {
      items: {
        "Lion": "Lion", "Elephant": "Elephant", "Giraffe": "Giraffe", "Zebra": "Zebra",
        "Polar Bear": "Polar Bear", "Penguin": "Penguin", "Seal": "Seal", "Arctic Fox": "Arctic Fox",
        "Monkey": "Monkey", "Parrot": "Parrot", "Toucan": "Toucan", "Jaguar": "Jaguar",
        "Dolphin": "Dolphin", "Shark": "Shark", "Whale": "Whale", "Octopus": "Octopus",
        "Savanna": "Savanna", "Arctic": "Arctic", "Rainforest": "Rainforest", "Ocean": "Ocean"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "Savanna": ["Lion", "Elephant", "Giraffe", "Zebra"],
          "Arctic": ["Polar Bear", "Penguin", "Seal", "Arctic Fox"],
          "Rainforest": ["Monkey", "Parrot", "Toucan", "Jaguar"],
          "Ocean": ["Dolphin", "Shark", "Whale", "Octopus"]
        }
      }
    }
  },
  {
    id: 'foods-to-cuisines',
    name: 'Foods → Cuisines',
    description: 'Match dishes to their cuisine origins (Category Mode)',
    icon: '🍜',
    data: {
      items: {
        "Sushi": "Sushi", "Ramen": "Ramen", "Tempura": "Tempura", "Miso Soup": "Miso Soup",
        "Pizza": "Pizza", "Pasta": "Pasta", "Risotto": "Risotto", "Gelato": "Gelato",
        "Taco": "Taco", "Burrito": "Burrito", "Quesadilla": "Quesadilla", "Guacamole": "Guacamole",
        "Croissant": "Croissant", "Baguette": "Baguette", "Crêpe": "Crêpe", "Macaron": "Macaron",
        "Japanese": "Japanese", "Italian": "Italian", "Mexican": "Mexican", "French": "French"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "Japanese": ["Sushi", "Ramen", "Tempura", "Miso Soup"],
          "Italian": ["Pizza", "Pasta", "Risotto", "Gelato"],
          "Mexican": ["Taco", "Burrito", "Quesadilla", "Guacamole"],
          "French": ["Croissant", "Baguette", "Crêpe", "Macaron"]
        }
      }
    }
  },
  {
    id: 'planets-to-types',
    name: 'Planets → Planet Types',
    description: 'Classify planets by their composition (Category Mode)',
    icon: '🪐',
    data: {
      items: {
        "Mercury": "Mercury", "Venus": "Venus", "Earth": "Earth", "Mars": "Mars",
        "Jupiter": "Jupiter", "Saturn": "Saturn",
        "Uranus": "Uranus", "Neptune": "Neptune",
        "Rocky Planets": "Rocky Planets", "Gas Giants": "Gas Giants", "Ice Giants": "Ice Giants"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "Rocky Planets": ["Mercury", "Venus", "Earth", "Mars"],
          "Gas Giants": ["Jupiter", "Saturn"],
          "Ice Giants": ["Uranus", "Neptune"]
        }
      }
    }
  },
  {
    id: 'instruments-to-families',
    name: 'Instruments → Families',
    description: 'Match musical instruments to their families (Category Mode)',
    icon: '🎸',
    data: {
      items: {
        "Violin": "Violin", "Cello": "Cello", "Guitar": "Guitar", "Harp": "Harp",
        "Trumpet": "Trumpet", "Trombone": "Trombone", "Tuba": "Tuba", "French Horn": "French Horn",
        "Flute": "Flute", "Clarinet": "Clarinet", "Oboe": "Oboe", "Bassoon": "Bassoon",
        "Drums": "Drums", "Xylophone": "Xylophone", "Tambourine": "Tambourine", "Cymbals": "Cymbals",
        "Strings": "Strings", "Brass": "Brass", "Woodwinds": "Woodwinds", "Percussion": "Percussion"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "Strings": ["Violin", "Cello", "Guitar", "Harp"],
          "Brass": ["Trumpet", "Trombone", "Tuba", "French Horn"],
          "Woodwinds": ["Flute", "Clarinet", "Oboe", "Bassoon"],
          "Percussion": ["Drums", "Xylophone", "Tambourine", "Cymbals"]
        }
      }
    }
  },
  {
    id: 'colors-to-types',
    name: 'Colors → Color Types',
    description: 'Classify colors as primary, secondary, or warm/cool (Category Mode)',
    icon: '🎨',
    data: {
      items: {
        "Red": "Red", "Blue": "Blue", "Yellow": "Yellow",
        "Orange": "Orange", "Green": "Green", "Purple": "Purple",
        "Cyan": "Cyan", "Magenta": "Magenta", "White": "White",
        "Primary Colors": "Primary Colors", "Secondary Colors": "Secondary Colors", "Tertiary Colors": "Tertiary Colors"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "Primary Colors": ["Red", "Blue", "Yellow"],
          "Secondary Colors": ["Orange", "Green", "Purple"],
          "Tertiary Colors": ["Cyan", "Magenta", "White"]
        }
      }
    }
  },
  {
    id: 'sports-to-categories',
    name: 'Sports → Categories',
    description: 'Classify sports by their type (Category Mode)',
    icon: '⚽',
    data: {
      items: {
        "Soccer": "Soccer", "Basketball": "Basketball", "Baseball": "Baseball", "Hockey": "Hockey",
        "Tennis": "Tennis", "Badminton": "Badminton", "Table Tennis": "Table Tennis", "Squash": "Squash",
        "Swimming": "Swimming", "Running": "Running", "Cycling": "Cycling", "Rowing": "Rowing",
        "Team Sports": "Team Sports", "Racket Sports": "Racket Sports", "Individual Sports": "Individual Sports"
      },
      metadata: {
        matchingMode: 'category' as const,
        categories: {
          "Team Sports": ["Soccer", "Basketball", "Baseball", "Hockey"],
          "Racket Sports": ["Tennis", "Badminton", "Table Tennis", "Squash"],
          "Individual Sports": ["Swimming", "Running", "Cycling", "Rowing"]
        }
      }
    }
  }
];

// Topic categories organized by matching mode
export const topicCategories: TopicCategory[] = [
  {
    id: 'math',
    name: 'Mathematics',
    description: 'Formulas, tables, and algebraic expressions',
    icon: '🧮',
    games: mathGames
  },
  {
    id: 'history',
    name: 'History',
    description: 'Historical dates, events, and figures',
    icon: '🏛️',
    games: historyGames
  },
  {
    id: 'language',
    name: 'Language',
    description: 'Characters, vocabulary, and translations',
    icon: '🗣️',
    games: languageGames
  },
  {
    id: 'science',
    name: 'Science',
    description: 'Scientific elements and concepts',
    icon: '🔬',
    games: scienceGames
  },
  {
    id: 'miscellaneous',
    name: 'Miscellaneous',
    description: 'Various topics including animals, countries, and numbers',
    icon: '🎯',
    games: miscellaneousGames
  }
];

// Helper function to get topics filtered by matching mode
export function getTopicsByMode(mode: 'one-to-one' | 'category' | 'sort' | 'multiple-choice'): TopicCategory[] {
  if (mode === 'multiple-choice') {
    // Multiple choice has its own setup flow, not topic-based
    return [];
  }
  if (mode === 'sort') {
    return [
      {
        id: 'sort-classify',
        name: 'Sort & Classify',
        description: 'Drag items one-by-one to correct category labels',
        icon: '🏷️',
        games: sortGames
      }
    ];
  } else if (mode === 'category') {
    return [
      {
        id: 'category-matching',
        name: 'Pair & Classify',
        description: 'Match two items and their category',
        icon: '📂',
        games: categoryGames
      }
    ];
  } else {
    // one-to-one mode: return all standard topics
    return topicCategories;
  }
}

// Flatten all games for backward compatibility (including sort and category games)
export const builtInDictionaries: GameCategory[] = [
  ...sortGames,
  ...categoryGames,
  ...mathGames,
  ...historyGames,
  ...languageGames,
  ...scienceGames,
  ...miscellaneousGames
];

// Helper to detect if dictionary is category mode
export function isCategoryDictionary(dictionary: any): boolean {
  return dictionary?.metadata?.matchingMode === 'category' ||
         (dictionary?.items && dictionary?.metadata?.categories);
}

// Helper to detect if dictionary is sort mode
export function isSortDictionary(dictionary: any): boolean {
  return dictionary?.metadata?.matchingMode === 'sort' ||
         (dictionary?.labels && dictionary?.items && !dictionary?.metadata?.categories);
}

// Helper to get the matching mode of a dictionary
export function getMatchingMode(dictionary: any): 'one-to-one' | 'category' | 'sort' {
  if (isSortDictionary(dictionary)) {
    return 'sort';
  }
  if (isCategoryDictionary(dictionary)) {
    return 'category';
  }
  return 'one-to-one';
}

export function getMaxPairsInDictionary(dictionary: any): number {
  // Check if this is a sort dictionary
  if (isSortDictionary(dictionary)) {
    // For sort mode, each item is a "pair" (item → label)
    return Object.keys(dictionary.items || {}).length;
  }

  // Check if this is a category dictionary
  if (isCategoryDictionary(dictionary)) {
    // For category mode, count total items across all categories
    const categories = dictionary.metadata?.categories || {};
    let totalItems = 0;
    Object.values(categories).forEach((items: any) => {
      if (Array.isArray(items)) {
        totalItems += items.length;
      }
    });
    // Return half because we match 2 items at a time
    return Math.floor(totalItems / 2);
  }

  // For one-to-one mode, use original logic
  // Get all unique pairs (avoid duplicates like "cat"→"🐱" and "🐱"→"cat")
  const dict = dictionary.items || dictionary;
  const processedPairs = new Set<string>();
  let pairCount = 0;

  Object.entries(dict).forEach(([key, value]) => {
    const pairId = [key, String(value)].sort().join('|');
    if (!processedPairs.has(pairId)) {
      processedPairs.add(pairId);
      pairCount++;
    }
  });

  return pairCount;
}

export function getRandomPairs(dictionary: Record<string, string> | CategoryDictionaryData | any, count: number): string[] {
  // If it's a category dictionary, extract the items portion
  const dict: Record<string, string> = (dictionary as any)?.items || dictionary;

  // Get all unique pairs (avoid duplicates like "cat"→"🐱" and "🐱"→"cat")
  const processedPairs = new Set<string>();
  const allPairs: Array<[string, string]> = [];

  Object.entries(dict).forEach(([key, value]) => {
    if (typeof value === 'string') {
      const pairId = [key, value].sort().join('|');
      if (!processedPairs.has(pairId)) {
        processedPairs.add(pairId);
        allPairs.push([key, value]);
      }
    }
  });

  // Shuffle and select the requested number of pairs
  const shuffledPairs = allPairs.sort(() => Math.random() - 0.5);
  const selectedPairs = shuffledPairs.slice(0, count);

  // Return both items from each pair as separate game items
  const gameItems: string[] = [];
  selectedPairs.forEach(([item1, item2]) => {
    gameItems.push(item1, item2);
  });

  return gameItems;
}